/**
 * Single page pay link checkout
 * Used for Fixed, Custom amount, Single-select list pay links
 */

import { useState, useEffect, useRef, useContext, useCallback } from "preact/hooks";
import { route } from "preact-router";
import { Text } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { formatIntegerCurrencyString, formatLocale } from "../../helpers/formatters";
import { getActionButtonStyle, isLightBackground } from "../../helpers/button";
import {
  CARD_TRANSACTION_FAILURE,
  ACH_TRANSACTION_FAILURE,
  ACH_PAYMENT_METHOD_SELECTED,
  CARD_PAYMENT_METHOD_SELECTED,
  EMIT_PAY_BUTTON_CLICK,
  EMIT_SUCCESS,
  FIELD_INPUT_FOCUSED,
  CARD_ON_FILE_FLAG_CHANGED,
  DEFAULT_ACH_PAYMENT_METHOD_LOAD,
  DEFAULT_CARD_PAYMENT_METHOD_LOAD,
  DEFAULT_CARD_WITH_SURCHARGE_PAYMENT_METHOD_LOAD,
} from "../../helpers/event-definitions";
import {
  CARD_AGREEMENT_STYLES,
  COLLECT_OPTIONS,
  getCollectFieldsFromOrder,
  getEcommerceTransactionOptions,
  validateOrder,
} from "../../helpers/collect";
import { getOrderTotal } from "../../helpers/order";
import { composeArrayKeysToObject } from "../../helpers/common";
import { getBillingAddresses } from "../../helpers/addresses-fields/billing";
import { getShippingAddresses } from "../../helpers/addresses-fields/shipping";

import { PaymentMethod } from "../../enums/paymentMethod";
import { TransactionStatus } from "../../enums/tranasctionStatus";
import { FundingSourceType } from "../../enums/fundingSourceType";

import { charge } from "../../rest/charge";
import { calculateSurchargeFee } from "../../rest/fees";

import "../../style/PoyntCollectForm.css";

import PaymentAgreement from "./PaymentAgreement";

const CardCollect = ({
  payLink,
  order,
  product,
  isWorldpay,
  setTransaction,
  setSurchargeData,
  setShowSurchargeConfirmation,
  onCollectError,
  showError,
  charging,
  setCharging,
  trackCustomEvent,
  paymentRequestIdToPay = '',
}) => {
  const [disabled, setDisabled] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [fieldInputFocused, setFieldInputFocused] = useState(null);
  const [saveCardOnFile, setSaveCardOnFile] = useState(null);
  const [hasSaveCardOnFileChanged, setHasSaveCardOnFileChanged] = useState(false);
  const [achTransactionError, setAchTransactionError] = useState(null);
  const [cardTransactionError, setCardTransactionError] = useState(null);

  const paymentRequestIdRef = useRef(paymentRequestIdToPay);

  useEffect(() => {
    paymentRequestIdRef.current = paymentRequestIdToPay;
  }, [paymentRequestIdToPay]);

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isDemoMode = urlParams.get("isSampleInvoice") === "true";

  const configsContext = useContext(Configs);

  // use a ref for permanent component state
  // that shouldn't trigger rerender on change
  const collectRef = useRef();

  /**
   * Triggered when Collect is validated
   * @param {Event} event
   */
  function onValidated(event) {
    setDisabled(!event.data.validated);
  }

  /**
   * Triggered when payment method is selected
   * @param {Event} event
   */
  function onPaymentMethodSelected(event) {
    setPaymentMethod(event?.data?.paymentMethod || null);
  }

  /**
   * Triggered when FieldInput is in focus state
   * @param {Event} event
   */
  function onFieldInputFocused(event) {
    setFieldInputFocused(event?.data?.fieldName || null);
  }

  useEffect(() => {
    if (trackCustomEvent && paymentMethod === PaymentMethod.ACH) {
      trackCustomEvent(ACH_PAYMENT_METHOD_SELECTED, { isSampleInvoice: isDemoMode });
    }
  }, [paymentMethod]);

  useEffect(() => {
    if (trackCustomEvent && paymentMethod === PaymentMethod.CARD) {
      trackCustomEvent(CARD_PAYMENT_METHOD_SELECTED, { isSampleInvoice: isDemoMode });
    }
  }, [paymentMethod]);

  useEffect(() => {
    if (isDemoMode && trackCustomEvent && fieldInputFocused) {
      trackCustomEvent(FIELD_INPUT_FOCUSED, {
        isSampleInvoice: isDemoMode,
        fieldName: fieldInputFocused,
      });
      setFieldInputFocused(null);
    }
  }, [isDemoMode, fieldInputFocused]);

  useEffect(() => {
    if (isDemoMode && trackCustomEvent && hasSaveCardOnFileChanged) {
      trackCustomEvent(CARD_ON_FILE_FLAG_CHANGED, {
        isSampleInvoice: isDemoMode,
        value: saveCardOnFile ?? false,
      });
    }
  }, [isDemoMode, hasSaveCardOnFileChanged, saveCardOnFile]);

  useEffect(() => {
    if (trackCustomEvent && cardTransactionError) {
      trackCustomEvent(CARD_TRANSACTION_FAILURE, {
        error: cardTransactionError,
        isSampleInvoice: isDemoMode,
      });
    }
  }, [cardTransactionError, isDemoMode]);

  useEffect(() => {
    if (trackCustomEvent && achTransactionError) {
      trackCustomEvent(ACH_TRANSACTION_FAILURE, {
        error: achTransactionError,
        isSampleInvoice: isDemoMode,
      });
    }
  }, [achTransactionError, isDemoMode]);

  /**
   * Triggered when card on file flag is changed
   * @param {Event} event
   */
  function onCardOnFileFlagChanged(event) {
    setSaveCardOnFile(event?.data?.value || null);
    setHasSaveCardOnFileChanged(true);
  }

  /**
   * Triggered when nonce is ready
   * @param {Event} event
   */
  const onNonce = useCallback(async (event) => {
    try {
      // if nonce not returned, fail
      if (!event?.data?.nonce) {
        throw new Error();
      }

      showError(false);
      setCharging(true);
      setCardTransactionError(null);
      setAchTransactionError(null);

      const chargeOptions = {
        ...getEcommerceTransactionOptions(
          configsContext?.configs,
          payLink,
          order,
          product,
          event.data,
          {
            paymentRequestIdToPay: paymentRequestIdRef.current
          },
        ),
        emailAddress: event.data.emailAddress,
        phone: event.data.phone,
        nonce: event.data.nonce,
        ...(payLink?.metadata?.allowSaveCard &&
        event.data.cardOnFile &&
        event.data.cardAgreement
          ? {
            savedCard: {
              cardOnFile: event.data.cardOnFile,
              cardAgreement: event.data.cardAgreement,
              recurringPlanId: event.data.recurringPlanId,
            },
          }
          : {}),
        ...(payLink?.enableACHPayment === "1" &&
        payLink.metadata?.paymentMethods &&
        event.data.fundingSourceType
          ? {
            fundingSourceType: event.data.fundingSourceType,
          }
          : {}),
        isPayableDomain: window.location.href.includes('//pay.'),
        paymentRequestIdToPay: paymentRequestIdRef.current
      };

      // calculate surcharge fee
      if (
        !isWorldpay &&
        payLink.isSurchargeFeatureEnabled &&
        event.data.fundingSourceType === FundingSourceType.CREDIT_DEBIT
      ) {
        const surchargeFee = await calculateSurchargeFee(configsContext?.configs?.env, chargeOptions);

        if (surchargeFee?.amount > 0) {
          setSurchargeData({ surchargeFee, chargeOptions, event });
          setShowSurchargeConfirmation(true);

          setCharging(false);

          return;
        }
      }

      const transaction = await charge(configsContext?.configs?.env, chargeOptions);

      setCharging(false);
      setTransaction(transaction);

      if (trackCustomEvent) {
        trackCustomEvent(EMIT_SUCCESS, { isSampleInvoice: isDemoMode, godaddyShopperId: window?.payLink?.godaddyShopperId });
      }

      route("/transaction-complete");

      // Metrics.track(MetricEvents.ORDER_COMPLETED_POYNT_APPLE_PAY_IMPRESSION);
    } catch (error) {
      console.log("onNonce error", error, error && error.stack);
      showError("Transaction failed. Please try again.");

      if (event.data.fundingSourceType === FundingSourceType.CHEQUE) {
        setAchTransactionError(error?.message || "Transaction declined");
      } else {
        setCardTransactionError(error?.message || "Transaction declined");
      }
    }
  }, []);

  // bootstrap collect if it's not there
  useEffect(() => {
    if (!collectRef.current) {
      const collect = new TokenizeJs(
        {
          businessId: configsContext?.configs?.businessId,
          storeId: configsContext?.configs?.storeId,
          applicationId: payLink.applicationId,
        }
      );

      const isAddressRequired = payLink.metadata?.isAddressRequired;
      const collectBillingAddress = payLink.metadata?.collectBillingAddress;
      const collectShippingAddress = payLink.metadata?.collectShippingAddress;
      const billingAddressesObj = getBillingAddresses(isAddressRequired && collectBillingAddress);
      const shippingAddressesObj = getShippingAddresses(
        isAddressRequired && collectShippingAddress,
      );
      const params = new URLSearchParams(window?.location?.search);
      const showACH =
        payLink?.payfacResellerEcheck === "1" &&
        payLink?.enableACHPayment === "1" &&
        payLink?.allowACH === "1" &&
        payLink?.disableACH !== "1" &&
        payLink?.metadata?.paymentMethods;

      const shouldShowACHAsDefault =
        showACH &&
        payLink?.isACHDefaultFeatureEnabled &&
        !payLink.isSurchargeFeatureEnabled;

      const masMultiplePaymentMethods = payLink?.metadata?.paymentMethods?.length > 1;
      const defaultPaymentMethod = shouldShowACHAsDefault ? PaymentMethod.ACH : PaymentMethod.CARD;

      if (trackCustomEvent && masMultiplePaymentMethods) {
        if (shouldShowACHAsDefault) {
          trackCustomEvent(DEFAULT_ACH_PAYMENT_METHOD_LOAD, {
            isSampleInvoice: isDemoMode,
          });
        } else {
          if (payLink.isSurchargeFeatureEnabled) {
            trackCustomEvent(DEFAULT_CARD_WITH_SURCHARGE_PAYMENT_METHOD_LOAD, {
              isSampleInvoice: isDemoMode,
            });
          } else {
            trackCustomEvent(DEFAULT_CARD_PAYMENT_METHOD_LOAD, {
              isSampleInvoice: isDemoMode,
            });
          }
        }
      }

      const collectOptions = {
        ...COLLECT_OPTIONS,
        iFrame: {
          ...COLLECT_OPTIONS.iFrame,
          height: payLink.metadata?.allowSaveCard ? "550px" : "485px",
        },
        displayComponents: {
          ...COLLECT_OPTIONS.displayComponents,
          ...(payLink.metadata?.allowPhone ? { phone: true } : {}),
          collectShippingAddress: collectShippingAddress,
          shippingAddressSameAsBilling: collectBillingAddress,
          showShippingAddressSameAsBillingCheckbox: collectBillingAddress,
          ...composeArrayKeysToObject(
            billingAddressesObj.displayComponentsKeys,
            collectBillingAddress,
          ),
          ...composeArrayKeysToObject(
            shippingAddressesObj.displayComponentsKeys,
            collectShippingAddress,
          ),
          ...((showACH || payLink.isSurchargeFeatureEnabled) ? { paymentNote: true } : {}),
        },
        demoMode: isDemoMode,
        additionalFieldsToValidate: [
          ...COLLECT_OPTIONS.additionalFieldsToValidate,
          ...(payLink.metadata?.allowPhone && payLink.metadata?.requirePhone ? ["phone"] : []),
          ...billingAddressesObj.fieldsToValidate,
          ...shippingAddressesObj.fieldsToValidate,
        ],
        fields: getCollectFieldsFromOrder(order),
        locale: formatLocale(configsContext?.configs?.ecommerceLocaleLanguage),
        ...(payLink.metadata?.allowSaveCard
          ? {
              enableCardOnFile: true,
              cardAgreementOptions: {
                businessName: configsContext?.configs?.ecommerceBusinessName,
                businessWebsite: configsContext?.configs?.businessWebsite,
                businessPhone: configsContext?.configs?.businessPhone,
                hideBusinessWebsite: true,
                hideBusinessPhone: true,
                style: {
                  ...CARD_AGREEMENT_STYLES,
                  acceptButton: {
                    ...CARD_AGREEMENT_STYLES.acceptButton,
                    "background-color": configsContext?.configs?.ecommerceCheckoutButtonColor,
                    color: isLightBackground(configsContext?.configs?.ecommerceCheckoutButtonColor)
                      ? "black"
                      : "white",
                  },
                },
              },
            }
          : {}),
        ...(showACH
          ? {
              paymentMethods: payLink.metadata.paymentMethods,
              style: {
                theme: "paylink",
              },
              customCss: {}
            }
          : {}),
        enableSurchargeFee: payLink.isSurchargeFeatureEnabled,
        defaultPaymentMethod: defaultPaymentMethod,
      };

      collect.mount("poynt-collect", document, collectOptions);

      collect.on("validated", onValidated);
      collect.on("nonce", onNonce);
      collect.on("error", onCollectError);

      collect.on("iframe_height_change", (event) => {
        if (event?.data?.height) {
          const iFrame = document.getElementById("poynt-collect-v2-iframe");
          iFrame?.style?.setProperty("height", event.data.height + 1 + "px");
        }
      });

      collect.on("payment_method_selected", onPaymentMethodSelected);
      collect.on("field_input_focused", onFieldInputFocused);
      collect.on("card_on_file_flag_changed", onCardOnFileFlagChanged);

      if (payLink?.metadata?.allowSaveCard) {
        collect.on("card_on_file_error", (event) => {
          showError(event?.data?.error);
        });
      }

      collectRef.current = collect;

      return () => {
        collect.off?.('nonce', onNonce);
      };
    }
  }, [onNonce]);

  function handleCharge() {
    const error = validateOrder(configsContext?.configs, payLink, order);
    if (error) {
      showError(error);
      throw new Error(error);
    }

    showError(false);
    setCharging(true);

    collectRef.current.getNonce({
      businessId: configsContext?.configs?.businessId,
    });

    if (trackCustomEvent) {
      trackCustomEvent(EMIT_PAY_BUTTON_CLICK, { isSampleInvoice: isDemoMode });
    }
  }

  // custom background color on the pay button
  let payButtonStyle;
  if (!disabled) {
    payButtonStyle = getActionButtonStyle(configsContext?.configs?.ecommerceCheckoutButtonColor);
  }

  const paymentRequests = payLink?.invoice?.paymentRequests ?? [];

  const selectedPaymentRequest = paymentRequests.find(
    paymentRequest => paymentRequest.paymentRequestId === paymentRequestIdToPay
  );

  const amountToPay = selectedPaymentRequest?.amount
    ? selectedPaymentRequest?.amount + order?.order?.value?.totals?.tipAmount
    : getOrderTotal(order);

  const amountString = formatIntegerCurrencyString(
    amountToPay,
    payLink.currency,
    configsContext?.configs?.ecommerceLocaleLanguage,
  );

  return (
    <Fragment>
      <div id="response"></div>
      {charging ? (
        <div className="pay-button-container">
          <button className="pay-button" style={payButtonStyle} disabled={true}>
            <Text id="PAYMENT_BUTTON_CHARGING">Charging...</Text>
          </button>
        </div>
      ) : (
        <div className="pay-button-container">
          <button
            onClick={handleCharge}
            className="pay-button"
            style={payButtonStyle}
            disabled={disabled}
          >
            {payLink.isSurchargeFeatureEnabled ? (
              <Text id="PAYMENT_BUTTON_CONTINUE">Continue</Text>
            ) : (
              <Text id="PAYMENT_BUTTON_AMOUNT" fields={{ amount: amountString }}>
                Pay {amountString}
              </Text>
            )}
          </button>
        </div>
      )}
      <PaymentAgreement
        payLink={payLink}
        order={order}
        paymentMethod={paymentMethod}
        saveCardOnFile={saveCardOnFile}
      />
    </Fragment>
  );
};

export default CardCollect;
